// src/data/salesOrders.ts
// -----------------------
// Repository for `request_sales_orders` (Unleashed Sales Order lines, db-62). The
// table is FLAT — one row per order line, with the header fields repeated on each
// line. These helpers group rows by `order_number` so the admin viewer shows one
// entry per sales order with its lines. Typed via getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type SalesOrderLine = Database["public"]["Tables"]["request_sales_orders"]["Row"];
type SalesOrderInsert = Database["public"]["Tables"]["request_sales_orders"]["Insert"];
type WriteResult = { error: { message: string } | null };

// The SO output shape (see src/services/soRulesEngine.ts) — kept loose so this
// repo doesn't depend on the service.
interface OrderHeaderLike {
  order_number: string;
  customer_code: string;
  customer_reference: string | null;
  warehouse_code: string | null;
  comments?: string | null; // the SO comments block (child codes, caseworker, delivery)
  order_date?: string | null;
  required_date?: string | null;
  quote_expiry_date?: string | null;
  sales_order_group?: string | null;
  sales_person_email?: string | null;
  // Structured delivery block (from the request's chosen delivery_addresses row).
  delivery_contact?: string | null;
  delivery_name?: string | null;
  delivery_street_address?: string | null;
  delivery_street_address_2?: string | null;
  delivery_suburb?: string | null;
  delivery_city?: string | null;
  delivery_region?: string | null;
  delivery_post_code?: string | null;
  delivery_country?: string | null;
  delivery_instruction?: string | null;
}
interface OrderLineLike {
  line_number: number;
  product_code: string;
  order_quantity: number;
  unit_price: number;
  line_comments: string | null;
}

// Insert the flat request_sales_orders rows for one order — the header fields are
// repeated on every line (as the Unleashed CSV template requires). Logs on error
// and returns { error }; a missing client returns a null error.
export async function createSalesOrder(
  header: OrderHeaderLike,
  lines: OrderLineLike[],
  requestId: number
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  if (lines.length === 0) return { error: null };
  const rows = lines.map((l) => ({
    request_id: requestId,
    order_number: header.order_number,
    customer_code: header.customer_code,
    customer_reference: header.customer_reference,
    warehouse_code: header.warehouse_code,
    comments: header.comments ?? null,
    order_date: header.order_date ?? null,
    required_date: header.required_date ?? null,
    quote_expiry_date: header.quote_expiry_date ?? null,
    sales_order_group: header.sales_order_group ?? null,
    sales_person_email: header.sales_person_email ?? null,
    delivery_contact: header.delivery_contact ?? null,
    delivery_name: header.delivery_name ?? null,
    delivery_street_address: header.delivery_street_address ?? null,
    delivery_street_address_2: header.delivery_street_address_2 ?? null,
    delivery_suburb: header.delivery_suburb ?? null,
    delivery_city: header.delivery_city ?? null,
    delivery_region: header.delivery_region ?? null,
    delivery_post_code: header.delivery_post_code ?? null,
    delivery_country: header.delivery_country ?? null,
    delivery_instruction: header.delivery_instruction ?? null,
    line_number: l.line_number,
    product_code: l.product_code,
    order_quantity: l.order_quantity,
    unit_price: l.unit_price,
    line_comments: l.line_comments,
  }));
  const { error } = await c.from("request_sales_orders").insert(rows as SalesOrderInsert[]);
  if (error) logger.warn("createSalesOrder failed:", error.message);
  return { error };
}

// Stamp each SO's nominated number onto its device/fee line records, via the db-71
// SECURITY DEFINER RPC `set_line_so_numbers` (so it doesn't need an RLS UPDATE policy
// on the child tables). `devices`/`fees` are [{ id, soNumber }]. No-op on empty / no
// client; logs + returns { error } on failure (best-effort at Confirm & Send).
export async function writeLineSoNumbers(
  devices: { id: string; soNumber: string }[],
  fees: { id: string; soNumber: string }[]
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  if (devices.length === 0 && fees.length === 0) return { error: null };
  const { error } = await c.rpc("set_line_so_numbers", {
    p_devices: devices.map((d) => ({ id: d.id, so: d.soNumber })),
    p_fees: fees.map((f) => ({ id: f.id, so: f.soNumber })),
  });
  if (error) logger.warn("writeLineSoNumbers failed:", error.message);
  return { error };
}

// Write a PICKER's corrected fields — the actual charged amount + the supplier
// ORDER number — onto the device/fee line records, via the db-117 SECURITY DEFINER
// RPC `set_line_picker_fields`. This is the EDIT-WIPE-SAFE path: a targeted UPDATE by
// row id, NOT the save_request_children delete-and-reinsert (which would wipe the
// SO/order numbers). `devices`/`fees` are [{ id, amount?, orderNumber? }]; only
// provided (non-null) fields are written. No-op on empty / no client; best-effort.
export async function writeLinePickerFields(
  devices: {
    id: string;
    amount?: number | null;
    orderNumber?: string | null;
    productCode?: string | null;
  }[],
  fees: { id: string; amount?: number | null; orderNumber?: string | null }[]
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  if (devices.length === 0 && fees.length === 0) return { error: null };
  // set_line_picker_fields is added by db-117 (device_product_code by db-126); cast
  // until gen:types picks it up so the branch typechecks before the migration is
  // applied locally.
  const { error } = await (
    c as unknown as {
      rpc: (fn: string, args: unknown) => Promise<{ error: { message: string } | null }>;
    }
  ).rpc("set_line_picker_fields", {
    p_devices: devices.map((d) => ({
      id: d.id,
      amount: d.amount ?? null,
      order_number: d.orderNumber ?? null,
      product_code: d.productCode ?? null,
    })),
    p_fees: fees.map((f) => ({
      id: f.id,
      amount: f.amount ?? null,
      order_number: f.orderNumber ?? null,
    })),
  });
  if (error) logger.warn("writeLinePickerFields failed:", error.message);
  return { error };
}

// Update one SO line's comment (request_sales_orders.line_comments) by row id.
// Pickers append their order number here at Completed; `request_sales_orders` has an
// internal-roles UPDATE policy (incl. picker), so this is a plain update. Best-effort.
export async function updateSalesOrderLineComment(
  id: string | number,
  comments: string
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("request_sales_orders")
    .update({ line_comments: comments })
    .eq("id", id as number);
  if (error) logger.warn("updateSalesOrderLineComment failed:", error.message);
  return { error };
}

// Update one SO line's unit price (request_sales_orders.unit_price) by row id. A
// picker sets the ACTUAL cost at Completed, which must reach the SO line so the
// payload pushed to Unleashed reflects the real price (not the seeded estimate).
// Same internal-roles UPDATE policy as updateSalesOrderLineComment (incl. picker).
export async function updateSalesOrderLinePrice(
  id: string | number,
  unitPrice: number
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("request_sales_orders")
    .update({ unit_price: unitPrice })
    .eq("id", id as number);
  if (error) logger.warn("updateSalesOrderLinePrice failed:", error.message);
  return { error };
}

// Update one SO line's product_code (request_sales_orders.product_code) by row id. A
// picker chooses the ACTUAL Unleashed product code at Completed, replacing the mapped
// placeholder (e.g. DEV_LAP) so the pushed payload orders the right stock item. Same
// internal-roles UPDATE policy as updateSalesOrderLinePrice (incl. picker).
export async function updateSalesOrderLineProductCode(
  id: string | number,
  productCode: string
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("request_sales_orders")
    .update({ product_code: productCode })
    .eq("id", id as number);
  if (error) logger.warn("updateSalesOrderLineProductCode failed:", error.message);
  return { error };
}

// One sales order = a distinct order_number and its line rows.
export interface SalesOrder {
  orderNumber: string;
  header: SalesOrderLine; // line 1 — carries the shared header fields (customer, delivery, dates…)
  lines: SalesOrderLine[]; // all lines, ordered by line_number
  lineCount: number;
}

const LIST_LIMIT = 5000;

// Group flat rows into per-order_number SalesOrders, preserving the incoming order
// of first appearance (so a created_at-desc fetch yields newest-order-first).
function groupByOrder(rows: SalesOrderLine[]): SalesOrder[] {
  const byNumber = new Map<string, SalesOrderLine[]>();
  for (const r of rows) {
    const key = r.order_number ?? "";
    const list = byNumber.get(key);
    if (list) list.push(r);
    else byNumber.set(key, [r]);
  }
  const out: SalesOrder[] = [];
  byNumber.forEach((lines, orderNumber) => {
    const sorted = [...lines].sort((a, b) => (a.line_number ?? 0) - (b.line_number ?? 0));
    out.push({ orderNumber, header: sorted[0], lines: sorted, lineCount: sorted.length });
  });
  return out;
}

// All sales orders, newest first (by created_at). null on error / no client.
export async function listSalesOrders(): Promise<SalesOrder[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("request_sales_orders")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(LIST_LIMIT);
  if (error) {
    logger.warn("listSalesOrders failed:", error.message);
    return null;
  }
  return groupByOrder((data ?? []) as unknown as SalesOrderLine[]);
}

// One sales order by its order_number (lines ordered by line_number). null on
// error / no client / not found.
export async function getSalesOrderByNumber(orderNumber: string): Promise<SalesOrder | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("request_sales_orders")
    .select("*")
    .eq("order_number", orderNumber)
    .order("line_number", { ascending: true });
  if (error) {
    logger.warn("getSalesOrderByNumber failed:", error.message);
    return null;
  }
  const rows = (data ?? []) as unknown as SalesOrderLine[];
  if (rows.length === 0) return null;
  return { orderNumber, header: rows[0], lines: rows, lineCount: rows.length };
}
